/**
 * Fetches all pages of data from the specified API endpoint.
 *
 * The API is expected to support pagination via `page` and `size` query parameters,
 * and return a response with one of the following shapes:
 *
 * 1. Directly an array of items.
 * 2. An object with an "items" property (PaginatedResponse) e.g. { items: T[], total, page, size, pages }.
 * 3. An object with a "data" property containing the items.
 *
 * An optional access token can be provided to include in the request headers.
 *
 * @param {string} url - The API endpoint URL (without query parameters).
 * @param {number} [pageSize=100] - The number of items to fetch per page.
 * @param {string} [accessToken] - An optional access token to include in the Authorization header.
 * @param {boolean} [skip] - An optional flag to skip fetching data.
 * @param {boolean} [forceRefresh=false] - When true, bypasses browser cache to fetch fresh data.
 * @returns {Promise<any[]>} - A promise that resolves to an array of all fetched items.
 * @throws Will throw an error if any fetch request fails.
 */
export async function fetchPaginatedData(
  url: string,
  pageSize = 100,
  accessToken?: string | null,
  skip?: boolean,
  forceRefresh = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any[]> {
  if (skip) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let allItems: any[] = [];
  let page = 1;
  let totalPages = Infinity;

  while (page <= totalPages) {
    try {
      const headers = new Headers();

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      // Add cache control headers when forcing refresh
      if (forceRefresh) {
        headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
        headers.set('Pragma', 'no-cache');
        headers.set('Expires', '0');
      }

      // Add a timestamp to the URL to bust cache when forcing refresh
      const cacheBuster = forceRefresh ? `&_t=${Date.now()}` : '';

      const response = await fetch(
        `${url}?page=${page}&size=${pageSize}${cacheBuster}`,
        {
          headers,
          // Use cache: 'no-store' to bypass the browser cache completely
          cache: forceRefresh ? 'no-store' : 'default',
        }
      );

      if (!response.ok) {
        throw new Error(`Error fetching data: ${response.statusText}`);
      }

      const result = await response.json();

      // Check if the result is directly an array.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let items: any[] = [];
      if (Array.isArray(result)) {
        items = result;
        // If the API returns a plain array, assume it's the only page.
        totalPages = page;
      } else {
        // Try to extract the items from known properties.
        items = result.items || result.data || [];
        totalPages = result.pages || page;
      }

      allItems = allItems.concat(items);
      if (page >= totalPages) break;
      page++;
    } catch (error) {
      console.error('Error in fetchPaginatedData:', error);
      throw error;
    }
  }

  return allItems;
}
